import { inject, Injectable, signal, computed, effect } from '@angular/core';
import { AuthService } from '@brightside/core/auth';
import {
  OmniSearchProvider,
  OmniSearchResult,
} from './models/omni-search.models';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OmniSearchService {
  private _auth = inject(AuthService);

  /**
   * Internal signal that stores all registered providers
   */
  private _providers = signal<OmniSearchProvider[]>([]);

  /**
   * Computed, RBAC-filtered providers based on AuthService roles
   */
  readonly providers = computed(() => {
    const roles = this._auth.getUserGroups?.() ?? [];
    return this._providers().filter((p) =>
      p.rolesAllowed?.length
        ? p.rolesAllowed.some((r) => roles.includes(r))
        : true
    );
  });

  constructor() { }

  /**
   * Register a new search provider
   */
  registerProvider(provider: OmniSearchProvider) {
    const current = this._providers();
    const exists = current.find((p) => p.id === provider.id);
    if (!exists) {
      this._providers.set([...current, provider]);
    }
  }

  /**
   * Remove all providers (used when switching subapps or on logout)
   */
  clearProviders() {
    this._providers.set([]);
  }

  /**
   * Imperative getter (still supported)
   * RBAC-filtered and optionally scoped by subApp.
   */
  getProviders(subApp?: string): OmniSearchProvider[] {
    const filtered = this.providers(); // <-- reactive snapshot
    return subApp ? filtered.filter((p) => p.subApp === subApp) : filtered;
  }

  /**
   * Find a provider by its shortcut (e.g. @client or /conversation)
   */
  getProviderByShortcut(
    shortcut: string,
    subApp?: string
  ): OmniSearchProvider | undefined {
    const providers = this.getProviders(subApp);
    return providers.find(
      (p) => p.shortcut?.toLowerCase() === shortcut.toLowerCase()
    );
  }

  /**
   * Execute a search across all authorized providers (multi-provider mode)
   */
  searchAll(
    query: string,
    subApp?: string
  ): Observable<
    { provider: OmniSearchProvider; results: OmniSearchResult[] }[]
  > {
    const providers = this.getProviders(subApp);
    const searches = providers.map((p) =>
      p.search(query).pipe(
        map((results) => ({ provider: p, results })),
        catchError(() => of({ provider: p, results: [] }))
      )
    );
    return forkJoin(searches);
  }

  /**
   * Execute a search for a specific provider (single-provider mode)
   */
  searchWithProvider(
    providerId: string,
    query: string
  ): Observable<OmniSearchResult[]> {
    const provider = this._providers().find((p) => p.id === providerId);
    if (!provider) return of([]);
    return provider.search(query).pipe(catchError(() => of([])));
  }
}
